import React, { useMemo, useState } from "react";
import { Crosshair, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
// 🆕 2026-08-31 · Sekunden UND Minuten, aus derselben Quelle wie die
// Ankerliste und die Shot-Liste — sonst laufen die Formate auseinander
// und man rechnet zwischen den drei Tabellen um.
import { bothSpans } from "@/lib/videoTime";

/**
 * Anker-Arbeitsliste — welche Shots brauchen noch Klick-Anker, und wie gut
 * ist die Kamerapose dort schon?
 *
 * WARUM DIESE LISTE
 * Ein 10-Minuten-Broadcast hat ~53 Shots, aber nur die als `wide`
 * klassifizierten (Taktik-Cam) sind geometrisch verwertbar — beim
 * Referenzvideo 10 von 53, zusammen 48 % der Laufzeit. Anker gleichmäßig
 * über das Video zu verteilen hilft nicht: die Shot-Selbstkalibrierung
 * braucht MINDESTENS DREI Klick-Anker IM SELBEN SHOT (siehe
 * `_MIN_JOINT_ANCHORS` in backend/modules/shot_selfcal.py), sonst entsteht
 * für diesen Shot gar keine Kamerapose und jeder Pass darin fällt auf
 * Einzelanker-Geometrie zurück.
 *
 * WORTWAHL: die Bilanz nennt "Wide-Zeit", nicht "Spielzeit" — siehe den
 * Kommentar an der Berechnung. Die Summe der wide-Shots ist die
 * geometrisch verwertbare Zeit, nicht die gespielte.
 *
 * WARUM RMS UND NICHT DIE EINZEL-AMPEL
 * Die Ampel eines Ankers bewertet die Pose aus DIESEM EINEN Anker. Einzelne
 * Anker widersprechen sich erwartungsgemäß (VISION.md Prinzip 6) — im
 * Referenzvideo sind 5 von 7 geklickten Ankern in Shot 1 einzeln „rot",
 * während der Bündelausgleich daraus 6/7 Inlier bei 4,57 px macht. Roten
 * Einzelampeln nachzujagen ist deshalb verlorene Arbeit. Was zählt, ist der
 * Selfcal-RMS PRO SHOT — und genau den zeigt diese Liste.
 */

// Ab hier wird die Pose vom 3D-Fit nicht mehr akzeptiert. Entspricht
// SELFCAL_PROVIDER_GATE_PX / TRAJECTORY_POSE_GATE_PX in backend/.env
// (Default 15). Nicht über die API exponiert, daher hier gespiegelt.
const DEFAULT_GATE_PX = 15;

// Erfahrungswerte aus den echten Sequenzen: gut gelöste Shots landen bei
// 1,3–2,6 px, ein zu einseitig verteilter bei 4,6 px.
const GOOD_PX = 3;

const MIN_ANCHORS_PER_SHOT = 3;

// 🆕 05.09. · Ein-Anker-Zeitalter: Ist die Kamera des Videos schon
// bewiesen (≥3 Posen → Konsens; kongruent zur Engine-Näherung), genügt
// EIN Anker je Shot — die Liste rechnete sonst das Dreifache der echten
// Restarbeit vor („42 Klicks offen" statt 11).
function zielProShot(selfcal, kameraKonsens) {
  const nPosen = selfcal ? Object.keys(selfcal).length : 0;
  return (kameraKonsens?.n_shots >= 3 || nPosen >= 3)
    ? 1 : MIN_ANCHORS_PER_SHOT;
}

/** Konsens-Befund eines Shots, angereichert um den Median fürs Tooltip. */
function konsensFuer(kameraKonsens, sid) {
  const e = kameraKonsens?.je_shot?.[String(sid)];
  return e ? { ...e, median_m: kameraKonsens.median_m } : null;
}

/**
 * Zählt die für die Selbstkalibrierung verwertbaren Anker pro Shot.
 *
 * ACHTUNG, hier steckt die Fehlerquelle: die Regel muss exakt
 * `shot_selfcal._gather` spiegeln, sonst behauptet die Liste „fertig", wo
 * keine Pose existiert — schlimmer als keine Liste.
 *  • `dl_auto`-Anker zählen NICHT. Sie tragen keine Klicks
 *    (`image_points` leer), sind reine Modellausgabe und werden von
 *    `_gather` ausdrücklich übersprungen. In der Ankerliste sehen sie wie
 *    normale Anker aus — deshalb der doppelte Check über `zone_kind` UND
 *    `source`.
 *  • In der /anchors-Sicht ist `kind` immer "extra"; die echte Art steht
 *    in `zone_kind`.
 */
function countClickAnchors(anchors) {
  const per = new Map();
  for (const a of anchors || []) {
    if (a.zone_kind === "dl_auto" || a.source === "dl_auto") continue;
    if (a.status !== "joint" && a.status !== "seg_only") continue;
    if (a.shot_id == null) continue;
    const sid = Number(a.shot_id);
    per.set(sid, (per.get(sid) || 0) + 1);
  }
  return per;
}

/** Status eines Shots aus Klickzahl + Selfcal-Eintrag. */
function classify(nClicks, entry, gatePx, konsens,
                  ziel = MIN_ANCHORS_PER_SHOT) {
  const missing = Math.max(0, ziel - nClicks);
  const rms = entry ? (entry.rms_inlier_px ?? entry.rms_px ?? null) : null;

  // Veraltet: der Eintrag stammt aus einem Ankerbestand, den es nicht mehr
  // gibt (Anker gelöscht, Shots neu erkannt, oder der Auto-Lauf hing).
  // Ohne diese Prüfung erscheint so ein Shot als erledigt.
  const stale = !!entry && entry.n_anchors != null
    && Number(entry.n_anchors) !== nClicks;

  if (stale) {
    return { key: "stale", rms, missing,
      label: "veraltet", hint: `Eintrag nennt ${entry.n_anchors} Anker, real sind ${nClicks}`,
      tone: "text-orange-300", rank: 1 };
  }
  if (!entry || rms == null) {
    return { key: "open", rms: null, missing,
      label: missing > 0 ? `${missing} fehlt` : "keine Pose",
      hint: missing > 0
        ? `${missing} weitere${missing === 1 ? "r" : ""} Klick-Anker bis zur Kamerapose`
        : "genug Anker, aber noch keine Selbstkalibrierung gelaufen",
      tone: "text-red-300", rank: 0 };
  }
  // 🆕 2026-08-30 · Kamera-Konsens VOR dem RMS. Eine Broadcast-Kamera steht
  // über ein Video hinweg still; wer weit daneben liegt, hat eine entartete
  // Pose — und zwar oft MIT grünem RMS. Referenzvideo: Shot 35 lag 75 m
  // neben dem Konsens bei 0,75 px, Shot 37 98 m bei 0,43 px. Beide hätten
  // hier ohne diese Prüfung „gut" gestanden.
  // Kein Urteil, ein Prüfhinweis: eine echte zweite Kamera erzeugt dieselbe
  // Abweichung zu Recht.
  if (konsens?.auffaellig) {
    return { key: "kamera_ab", rms, missing,
      label: "Kamera daneben",
      hint: `Pose setzt die Kamera ${konsens.abstand_m} m neben den Konsens `
        + `der übrigen Shots (${konsens.position_m?.join(" / ")} m gegen `
        + `${konsens.median_m?.join(" / ")} m). Bei ${rms != null ? rms.toFixed(1) : "?"} px RMS `
        + `sieht die Pose gut aus und ist trotzdem entartet — Anker breiter `
        + `verteilen. Gibt es hier wirklich eine zweite Kamera, ist der `
        + `Hinweis nach Sichtung erledigt.`,
      tone: "text-orange-300", rank: 1 };
  }
  if (rms > gatePx) {
    return { key: "over_gate", rms, missing,
      label: "über Gate", hint: `${rms.toFixed(1)} px > ${gatePx} px — Pose wird vom 3D-Fit verworfen`,
      tone: "text-red-300", rank: 2 };
  }
  if (rms > GOOD_PX) {
    return { key: "check", rms, missing,
      label: "prüfen", hint: `${rms.toFixed(1)} px — nutzbar, aber Anker breiter über den Shot verteilen hilft`,
      tone: "text-yellow-300", rank: 3 };
  }
  return { key: "good", rms, missing,
    label: "gut", hint: `${rms.toFixed(1)} px`, tone: "text-emerald-300", rank: 4 };
}

export default function AnchorWorklist({
  shots = [],
  anchors = [],
  selfcal = null,
  selfcalAuto = null,
  // 🆕 2026-08-30 · Kamera-Konsens aus der shot-selfcal-Route:
  // {median_m, n_shots, grenze_m, je_shot: {<sid>: {abstand_m, auffaellig}}}
  kameraKonsens = null,
  gatePx = DEFAULT_GATE_PX,
  onJump,
}) {
  const [wideOnly, setWideOnly] = useState(true);
  const [showDone, setShowDone] = useState(false);

  const rows = useMemo(() => {
    const per = countClickAnchors(anchors);
    const out = [];
    for (const s of shots) {
      const cls = s.class_effective || s.class_manual || s.class_auto?.label;
      if (wideOnly && cls !== "wide") continue;
      const sid = Number(s.shot_id);
      const start = Number(s.start_s) || 0;
      const end = Number(s.end_s) || 0;
      const dur = Math.max(0, end - start);
      const nClicks = per.get(sid) || 0;
      const entry = selfcal ? selfcal[String(sid)] || selfcal[sid] : null;
      const st = classify(nClicks, entry && !entry.reason ? entry : null, gatePx,
                          konsensFuer(kameraKonsens, sid),
                          zielProShot(selfcal, kameraKonsens));
      out.push({ sid, start, end, dur, nClicks, cls, ...st,
        // Nutzen = betroffene Spielzeit pro noch offenem Klick. Ein langer
        // Shot ohne Pose bringt mehr als ein 9-Sekunden-Schnipsel.
        gain: st.missing * dur });
    }
    out.sort((a, b) => (a.rank - b.rank)
      || (b.gain - a.gain)
      || ((b.rms || 0) - (a.rms || 0))
      || (b.dur - a.dur));
    return out;
  }, [shots, anchors, selfcal, wideOnly, gatePx, kameraKonsens]);

  // ── Wide-Zeit-Bilanz ────────────────────────────────────────────────
  // Bewusst IMMER über die wide-Shots gerechnet, auch wenn die Liste auf
  // "alle Shots" umgeschaltet ist — sonst wäre die Prozentzahl nicht
  // vergleichbar.
  //
  // WORTWAHL, bewusst NICHT "Spielzeit" (Nutzer-Korrektur 22.08.2026):
  // die Summe der wide-Shots ist NICHT die gespielte Zeit.
  //   * Während des Spiels wird live auch auf Nahkameras geschnitten —
  //     das ist Spielzeit, aber nicht wide.
  //   * Innerhalb eines wide-Shots kann das Spiel stehen: ein Call im
  //     Feld, Diskussion, dann Check zum Weiterspielen — wide, aber keine
  //     Spielzeit. Die np-Erkennung fängt nur längere Stehphasen (≥ 8 s
  //     bei geringer Bildbewegung).
  // Diese Zahl misst also die GEOMETRISCHE ABDECKUNG, nicht Spielzeit.
  // Wer später Punkte, Laufwege oder Wurfzahlen auswertet, darf sie nicht
  // als gespielte Zeit lesen.
  //
  // Dynamik: dieses useMemo hängt an `shots`, `anchors` und `selfcal`.
  // Alle drei werden nach jedem Anker-Save neu geladen (refresh() lädt
  // Anker UND Shots, pollSelfcalAuto() danach die Selfcal), und nach einer
  // Klassenänderung lädt loadShots() neu. Die Bilanz rechnet damit von
  // selbst nach — auch wenn ein Shot erst durch eine Umklassifizierung zu
  // Spielzeit wird.
  const bilanz = useMemo(() => {
    const per = countClickAnchors(anchors);
    let total = 0, mitPose = 0, ueberGate = 0, offen = 0;
    let nTotal = 0, nMitPose = 0;
    for (const sh of shots) {
      const cls = sh.class_effective || sh.class_manual || sh.class_auto?.label;
      if (cls !== "wide") continue;
      const sid = Number(sh.shot_id);
      const dur = Math.max(0, (Number(sh.end_s) || 0) - (Number(sh.start_s) || 0));
      const entry = selfcal ? selfcal[String(sid)] || selfcal[sid] : null;
      const st = classify(per.get(sid) || 0, entry && !entry.reason ? entry : null, gatePx,
                          konsensFuer(kameraKonsens, sid),
                          zielProShot(selfcal, kameraKonsens));
      total += dur; nTotal += 1;
      // rank 3 = prüfen, 4 = gut — beide liefern eine Pose, die der 3D-Fit
      // akzeptiert. rank 2 (über Gate) wird verworfen, zählt also nicht.
      if (st.rank >= 3) { mitPose += dur; nMitPose += 1; }
      else if (st.rank === 2) ueberGate += dur;
      else offen += dur;
    }
    return { total, mitPose, ueberGate, offen, nTotal, nMitPose,
             anteil: total > 0 ? mitPose / total : 0 };
  }, [shots, anchors, selfcal, gatePx, kameraKonsens]);

  const rechnet = selfcalAuto?.status === "running";

  const openRows = rows.filter((r) => r.rank <= 3);
  const doneRows = rows.filter((r) => r.rank === 4);
  const totalMissing = rows.reduce((n, r) => n + r.missing, 0);
  const noPose = rows.filter((r) => r.key === "open" || r.key === "stale").length;

  const jump = (r) => {
    if (typeof onJump === "function") onJump((r.start + r.end) / 2, r);
  };

  const Row = ({ r }) => (
    <button
      type="button"
      data-testid={`anchor-worklist-row-${r.sid}`}
      onClick={() => jump(r)}
      title={r.hint}
      className="w-full flex items-center gap-2 px-2 py-1 rounded-sm text-left
                 hover:bg-primary/10 focus:outline-none focus:ring-1 focus:ring-primary/50"
    >
      <span className="font-mono text-[10px] text-muted-foreground w-12 shrink-0">
        #{r.sid}
      </span>
      <span className="font-mono text-[10px] text-foreground/90 w-44 shrink-0">
        {bothSpans(r.start, r.end)}
      </span>
      <span className="font-mono text-[10px] text-muted-foreground w-12 shrink-0 text-right">
        {r.dur.toFixed(0)}s
      </span>
      <span className="font-mono text-[10px] text-muted-foreground w-12 shrink-0 text-right">
        {/* Ab dem Soll nur noch die Zahl — "7/3" liest sich wie ein
            kaputter Bruch. */}
        {r.nClicks >= MIN_ANCHORS_PER_SHOT
          ? `${r.nClicks} ✓`
          : `${r.nClicks}/${MIN_ANCHORS_PER_SHOT}`}
      </span>
      <span className={`font-mono text-[10px] w-16 shrink-0 text-right ${r.tone}`}>
        {r.rms != null ? `${r.rms.toFixed(1)} px` : "—"}
      </span>
      <span className={`font-mono text-[10px] uppercase tracking-wider flex-1 ${r.tone}`}>
        {r.label}
      </span>
    </button>
  );

  return (
    <div
      data-testid="anchor-worklist-panel"
      className="border border-primary/40 bg-primary/5 rounded-sm p-3 space-y-2"
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-primary">
            Anker-Arbeitsliste
          </div>
          <div className="font-mono text-[10px] text-muted-foreground">
            {zielProShot(selfcal, kameraKonsens) === 1
              ? "Ziel: 1 Klick-Anker pro Shot — Kamera ist bewiesen"
              : `Ziel: ${MIN_ANCHORS_PER_SHOT} Klick-Anker pro Shot (oder ~6 beliebig verteilt)`}
            {" · bewertet wird der Selfcal-RMS des Shots, nicht die Einzel-Ampel"}
          </div>
          <div
            data-testid="anchor-worklist-summary"
            className="font-mono text-[10px] text-foreground/90 pt-0.5"
          >
            {totalMissing > 0
              ? `${totalMissing} Klick${totalMissing === 1 ? "" : "s"} offen`
              : "alle Shots haben genug Anker"}
            {noPose > 0 && ` · ${noPose} Shot${noPose === 1 ? "" : "s"} ohne Pose`}
            {` · ${rows.length} Shot${rows.length === 1 ? "" : "s"} gelistet`}
          </div>
        </div>
        <Button
          data-testid="anchor-worklist-wide-toggle"
          size="sm" variant="ghost"
          onClick={() => setWideOnly((v) => !v)}
          className="font-mono text-[10px] uppercase tracking-widest whitespace-nowrap"
          title="Nur wide-Shots (Taktik-Cam) sind geometrisch verwertbar — Replay, Werbung und Nahaufnahmen brauchen keine Anker"
        >
          {wideOnly ? "nur wide" : "alle Shots"}
        </Button>
      </div>

      {bilanz.nTotal > 0 && (
        <div
          data-testid="anchor-worklist-balance"
          className="pt-1.5 border-t border-primary/20 space-y-1"
        >
          <div className="flex items-baseline justify-between gap-2">
            <span
              className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground"
              title={"Summe der wide-Shots mit gueltiger Kamerapose. NICHT die "
                + "gespielte Zeit: waehrend des Spiels wird live auch auf "
                + "Nahkameras geschnitten (Spielzeit, aber nicht wide), und "
                + "innerhalb eines wide-Shots kann das Spiel stehen (Call, "
                + "Diskussion, Check) — wide, aber keine Spielzeit."}
            >
              Wide-Zeit mit Kamerapose
            </span>
            <span className="font-mono text-[11px] text-foreground">
              <span className={bilanz.anteil >= 0.999 ? "text-emerald-300" : "text-primary"}>
                {Math.round(bilanz.mitPose)} s
              </span>
              <span className="text-muted-foreground"> / {Math.round(bilanz.total)} s</span>
              <span className="text-muted-foreground">
                {" "}· {Math.round(bilanz.anteil * 100)} %
              </span>
              {rechnet && (
                <span
                  data-testid="anchor-worklist-balance-pending"
                  className="text-cyan-300 animate-pulse"
                  title="Die Auto-Selbstkalibrierung läuft noch — der Wert kann gleich steigen"
                >
                  {" "}⟳
                </span>
              )}
            </span>
          </div>
          {/* Balken: grün = Pose vorhanden, rot = über Gate (verworfen),
              grau = noch ohne Pose */}
          <div className="flex h-1.5 w-full overflow-hidden rounded-sm bg-muted">
            <div className="bg-emerald-400/80"
                 style={{ width: `${(bilanz.mitPose / bilanz.total) * 100}%` }} />
            <div className="bg-red-400/70"
                 style={{ width: `${(bilanz.ueberGate / bilanz.total) * 100}%` }} />
          </div>
          <div className="font-mono text-[10px] text-muted-foreground">
            {bilanz.nMitPose} von {bilanz.nTotal} wide-Shots
            {bilanz.ueberGate > 0 && ` · ${Math.round(bilanz.ueberGate)} s über Gate (verworfen)`}
            {bilanz.offen > 0 && ` · ${Math.round(bilanz.offen)} s noch offen`}
            {rechnet && " · Selbstkalibrierung rechnet…"}
          </div>
        </div>
      )}

      {rows.length === 0 && (
        <div className="font-mono text-[10px] text-muted-foreground pt-1 border-t border-primary/20">
          Keine {wideOnly ? "wide-" : ""}Shots gefunden — erst Shots erkennen und
          klassifizieren.
        </div>
      )}

      {openRows.length > 0 && (
        <div className="space-y-0.5 pt-1 border-t border-primary/20">
          <div className="flex items-center gap-2 px-2 font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
            <span className="w-12 shrink-0">Shot</span>
            {/* w-44 wie die Datenzelle darunter — mit w-24 rutschte
                das mm:ss der Spanne optisch unter den "Dauer"-Kopf und
                las sich wie eine falsch beschriftete Spalte. */}
            <span className="w-44 shrink-0">Zeitfenster</span>
            <span className="w-12 shrink-0 text-right">Dauer</span>
            <span className="w-12 shrink-0 text-right">Anker</span>
            <span className="w-16 shrink-0 text-right">RMS</span>
            <span className="flex-1">Status</span>
          </div>
          {openRows.map((r) => <Row key={r.sid} r={r} />)}
        </div>
      )}

      {doneRows.length > 0 && (
        <div className="pt-1 border-t border-primary/20">
          <button
            type="button"
            data-testid="anchor-worklist-done-toggle"
            onClick={() => setShowDone((v) => !v)}
            className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-emerald-300/80 hover:text-emerald-300"
          >
            {showDone ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            {doneRows.length} Shot{doneRows.length === 1 ? "" : "s"} gut gelöst
          </button>
          {showDone && (
            <div className="space-y-0.5 pt-1">
              {doneRows.map((r) => <Row key={r.sid} r={r} />)}
            </div>
          )}
        </div>
      )}

      <div className="font-mono text-[9px] text-muted-foreground pt-1 border-t border-primary/20 leading-relaxed">
        <Crosshair className="w-3 h-3 inline mr-1" />
        Klick springt in die Shotmitte. Innerhalb eines Shots ist die Position
        frei wählbar — die Selbstkalibrierung bündelt alle Klicks des Shots zu
        einer Kameraposition. <span className="text-foreground/70">Breit
        verteilen</span> ist besser als ballen: Pan/Tilt/FOV werden zwischen den
        Ankern interpoliert, ohne Stützstellen am Shot-Ende wird dort
        extrapoliert.
      </div>
    </div>
  );
}
